import React, { useEffect, useState } from "react";

import ClientSection from "./payment_request/ClientSection";
import PaymentDetailsSection from "./payment_request/PaymentDetailsSection";
import InvoiceSection from "./payment_request/InvoiceSection";
import ReminderSection from "./payment_request/ReminderSection";

import "../../../../../css/pages/ca_pages/finances/models/client_payments_model/PaymentRequestModal.css";

/*
 * ============================================================================
 * CREATE EMPTY REMINDER
 * ============================================================================
 */

const createReminder = () => ({
  id: crypto.randomUUID(),

  date: "",
  time: "10:00",

  channels: ["email", "whatsapp"],

  messageMode: "default",

  customMessage: "",

  customizePlatforms: false,

  platformMessages: {
    email: { subject: "", message: "" },
    whatsapp: { message: "" },
    sms: { message: "" },
  },
});

/*
 * ============================================================================
 * INITIAL FORM
 * ============================================================================
 */

const initialForm = () => ({
  title: "",
  description: "",
  amount: "",
  due_date: "",

  currency: "INR",

  invoice: null,

  enable_reminders: true,

  reminders: [createReminder()],
});

/*
 * ============================================================================
 * NORMALIZE REMINDER FROM API
 * ============================================================================
 *
 * Backend example:
 *
 * {
 *     id: 10,
 *     scheduled_for: "...",
 *     message_mode: "default",
 *     custom_message: "",
 *     messages: [...]
 * }
 *
 * Frontend:
 *
 * {
 *     id,
 *     date,
 *     time,
 *     channels,
 *     messageMode,
 *     customMessage,
 *     customizePlatforms,
 *     platformMessages
 * }
 *
 * ============================================================================
 */

const normalizeReminderFromApi = (reminder) => {
  let date = "";
  let time = "10:00";

  /*
   * Convert scheduled_for into:
   *
   * YYYY-MM-DD
   * HH:mm
   */

  if (reminder?.scheduled_for) {
    const scheduled = new Date(reminder.scheduled_for);

    if (!Number.isNaN(scheduled.getTime())) {
      const year = scheduled.getFullYear();

      const month = String(scheduled.getMonth() + 1).padStart(2, "0");

      const day = String(scheduled.getDate()).padStart(2, "0");

      const hours = String(scheduled.getHours()).padStart(2, "0");

      const minutes = String(scheduled.getMinutes()).padStart(2, "0");

      date = `${year}-${month}-${day}`;

      time = `${hours}:${minutes}`;
    }
  }

  /*
   * Default platform messages.
   */

  const platformMessages = {
    email: {
      subject: "",
      message: "",
    },

    whatsapp: {
      message: "",
    },

    sms: {
      message: "",
    },
  };

  /*
   * Channels selected on the reminder.
   */

  const channels = [];

  /*
   * Convert backend messages into frontend structure.
   */

  if (Array.isArray(reminder?.messages)) {
    reminder.messages.forEach((message) => {
      const platform = message?.platform;

      if (!platform) {
        return;
      }

      if (!channels.includes(platform)) {
        channels.push(platform);
      }

      if (platform === "email") {
        platformMessages.email = {
          subject: message?.subject || "",
          message: message?.message || "",
        };
      }

      if (platform === "whatsapp") {
        platformMessages.whatsapp = {
          message: message?.message || "",
        };
      }

      if (platform === "sms") {
        platformMessages.sms = {
          message: message?.message || "",
        };
      }
    });
  }

  /*
   * If backend has no messages, use application defaults.
   */

  if (!channels.length) {
    channels.push("email", "whatsapp");
  }

  return {
    id: reminder?.id?.toString() || crypto.randomUUID(),

    date,

    time,

    channels,

    messageMode: reminder?.message_mode || "default",

    customMessage: reminder?.custom_message || "",

    customizePlatforms:
      Array.isArray(reminder?.messages) && reminder.messages.length > 0,

    platformMessages,
  };
};

/*
 * ============================================================================
 * NORMALIZE PAYMENT REQUEST FROM API
 * ============================================================================
 */

const normalizePaymentRequest = (paymentRequest) => {
  if (!paymentRequest) {
    return initialForm();
  }

  const reminders = Array.isArray(paymentRequest.reminder_schedules)
    ? paymentRequest.reminder_schedules.map(normalizeReminderFromApi)
    : [];

  return {
    title: paymentRequest.title || "",

    description: paymentRequest.description || "",

    amount:
      paymentRequest.amount != null ? paymentRequest.amount.toString() : "",

    due_date: paymentRequest.due_date
      ? paymentRequest.due_date.slice(0, 10)
      : "",

    currency: paymentRequest.currency || "INR",

    invoice: paymentRequest.invoice || null,

    /*
     * No reminder schedules means reminders are disabled.
     */

    enable_reminders: reminders.length > 0,

    reminders: reminders.length > 0 ? reminders : [createReminder()],
  };
};

/*
 * ============================================================================
 * PAYMENT REQUEST MODAL
 * ============================================================================
 */

const PaymentRequestModal = ({
   isOpen,
    onClose,
    onCreated,
    onUpdated,
    paymentRequest = null,

    createPaymentRequest,
    createPaymentRequestDraft,
    updatePaymentRequestDraft,
    updatePaymentRequest,
}) => {
  /*
   * ------------------------------------------------------------------------
   * LOCAL STATE
   * ------------------------------------------------------------------------
   */

  const [selectedClient, setSelectedClient] = useState(null);

  const [form, setForm] = useState(initialForm);

  const [errors, setErrors] = useState({});

  const [loading, setLoading] = useState(false);

  const [action, setAction] = useState(null);

  const [fetchingRequest, setFetchingRequest] = useState(false);

  /*
   * ------------------------------------------------------------------------
   * MODE
   * ------------------------------------------------------------------------
   */

  const isEditMode = Boolean(paymentRequest?.id);

  /*
   * ------------------------------------------------------------------------
   * LOAD PAYMENT REQUEST
   * ------------------------------------------------------------------------
   *
   * When the parent gives us:
   *
   * paymentRequest = { id: 123 }
   *
   * we retrieve the complete record from the server.
   *
   * This means the parent does NOT need to know how the
   * payment request is converted into form data.
   * ------------------------------------------------------------------------
   */

  useEffect(() => {
    if (!isOpen) { return; }

    /* CREATE MODE */
    if (!paymentRequest?.id) {
      setSelectedClient(null);
      setForm(initialForm());
      setErrors({});
      setAction(null);
      setFetchingRequest(false);

      return;
    }

    /* EDIT / VIEW MODE */
    setFetchingRequest(false);
    setErrors({});
    setAction(null);

    const data = paymentRequest;

    setForm(normalizePaymentRequest(data));

    setSelectedClient({
      id: data.client,
      name: data.client_name,
      email: data.client_email,
      contact: data.client_contact,
    });
  }, [isOpen, paymentRequest]);

  /*
   * ------------------------------------------------------------------------
   * IMPORTANT:
   *
   * DO NOT RENDER ANYTHING WHEN MODAL IS CLOSED.
   *
   * This prevents the popup from always appearing on the page.
   * ------------------------------------------------------------------------
   */

  if (!isOpen) { return null; }

  /* GENERIC FORM CHANGE */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  /*
   * ------------------------------------------------------------------------
   * UPDATE REMINDER
   * ------------------------------------------------------------------------
   */

  const updateReminder = (reminderId, changes) => {
    setForm((prev) => ({
      ...prev,

      reminders: prev.reminders.map((reminder) =>
        reminder.id === reminderId
          ? { ...reminder, ...changes }
          : reminder,
      ),
    }));
  };

  /* ADD REMINDER */

  const addReminder = () => {
    setForm((prev) => ({
      ...prev,

      reminders: [...prev.reminders, createReminder()],
    }));
  };

  /* REMOVE REMINDER */
  const removeReminder = (reminderId) => {
    setForm((prev) => ({
      ...prev,

      reminders: prev.reminders.filter(
        (reminder) => reminder.id !== reminderId,
      ),
    }));
  };

  /* DUPLICATE REMINDER */
  const duplicateReminder = (reminderId) => {
    setForm((prev) => {
      const reminder = prev.reminders.find((item) => item.id === reminderId);

      if (!reminder) {
        return prev;
      }

      const duplicate = {
        ...reminder,

        id: crypto.randomUUID(),

        // Deep-copy nested objects so editing the duplicate does not accidentally mutate the original.

        channels: [...(reminder.channels || [])],

        platformMessages: {
          email:    { ...reminder.platformMessages?.email },
          whatsapp: { ...reminder.platformMessages?.whatsapp },
          sms:      { ...reminder.platformMessages?.sms },
        },
      };

      return {
        ...prev,
        reminders: [...prev.reminders, duplicate],
      };
    });
  };

  /* CLIENT SELECTION */
  const handleClientSelect = (client) => {
    setSelectedClient(client);

    setErrors((prev) => ({
      ...prev,

      client: "",
    }));
  };

  /* FULL FORM VALIDATION */
  const validateForm = () => {
    const newErrors = {};

    // Client
    if (!selectedClient) {
      newErrors.client = "Please select a client.";
    }

    // Title
    if (!form.title.trim()) {
      newErrors.title = "Please enter a payment title.";
    }

    // Amount
    if (!form.amount || Number(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid payment amount.";
    }

    // Due date
    if (!form.due_date) {
      newErrors.due_date = "Please select a payment due date.";
    }

    // Reminders
    if (form.enable_reminders) {
      if (!form.reminders.length) {
        newErrors.reminders =
          "Please add at least one reminder or disable automatic reminders.";
      }

      form.reminders.forEach((reminder, index) => {
        if (!reminder.date) {
          newErrors[`reminder_${reminder.id}_date`] =
            `Please select a date for reminder ${index + 1}.`;
        }

        if (!reminder.time) {
          newErrors[`reminder_${reminder.id}_time`] =
            `Please select a time for reminder ${index + 1}.`;
        }

        if (
          reminder.messageMode === "custom" &&
          !reminder.customMessage.trim()
        ) {
          newErrors[`reminder_${reminder.id}_message`] =
            `Please enter a custom message for reminder ${index + 1}.`;
        }
      });
    }

    setErrors(newErrors);

    // Scroll to first error.
    if (Object.keys(newErrors).length > 0) {
      setTimeout(() => {
        const firstError = document.querySelector(".field-error");

        firstError?.scrollIntoView({
          behavior: "smooth",

          block: "center",
        });
      }, 50);

      return false;
    }

    return true;
  };

  // DRAFT VALIDATION
  const validateDraft = () => {
    const newErrors = {};

    // Client is required.
    if (!selectedClient) {
      newErrors.client = "Please select a client.";
    }

    // Title is required.
    if (!form.title.trim()) {
      newErrors.title = "Please enter a payment title.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  //BUILD PAYLOAD
  const buildPayload = () => {
    if (!selectedClient) { return null; }

    return {
      client: selectedClient.id,
      title: form.title.trim(),
      description: form.description.trim(),
      amount: form.amount,
      currency: form.currency || "INR",
      due_date: form.due_date || null,
      invoice: form.invoice || null,
      enable_reminders: Boolean(form.enable_reminders),

      reminders: form.enable_reminders
        ? form.reminders.map((reminder) => ({
            scheduled_for:
              reminder.date && reminder.time
                ? `${reminder.date}T${reminder.time}:00`
                : null,

            channels: reminder.channels || [],
            message_mode: reminder.messageMode || "default",
            custom_message: reminder.customMessage || "",
            customize_platforms: Boolean(reminder.customizePlatforms),
            platform_messages: reminder.platformMessages || {},
          }))
        : [],
    };
  };

  // CREATE PAYMENT REQUEST
  const handleCreateRequest = async (e) => {
    e?.preventDefault();

    if (loading || fetchingRequest) {
      return;
    }

    setErrors({});

    if (!validateForm()) {
      return;
    }

    const payload = buildPayload();

    if (!payload) { return; }

    try {
      setLoading(true);

      setAction("create");

      const response = await createPaymentRequest(payload);

      /*
       * Tell parent that a new request
       * was created.
       *
       * Parent can then call:
       *
       * getPaymentOverview()
       * getAllPaymentRequests()
       */

      onCreated?.(response?.data);

      resetForm();

      onClose?.();
    } catch (err) {
      console.error("Failed to create payment request:", err);

      setErrors({
        submit:
          err?.response?.data?.detail ||
          "Unable to create payment request. Please try again.",
      });
    } finally {
      setLoading(false);

      setAction(null);
    }
  };

  /*
   * ------------------------------------------------------------------------
   * SAVE AS DRAFT
   * ------------------------------------------------------------------------
   *
   * CREATE MODE:
   *
   * createPaymentRequestDraft(payload)
   *
   * EDIT MODE:
   *
   * updatePaymentRequest(id, payload)
   *
   * ------------------------------------------------------------------------
   */

  const handleSaveDraft = async () => {
    if (loading || fetchingRequest) {
      return;
    }

    console.log(
      "[PaymentRequestModal] Save as draft clicked",
    );

    setErrors({});

    if (!validateDraft()) {
      console.log(
        "[PaymentRequestModal] Draft blocked by validation",
      );

      return;
    }

    const payload = buildPayload();

    if (!payload) {
      console.error(
        "[PaymentRequestModal] Draft payload could not be created",
      );

      return;
    }

    try {
      setLoading(true);

      setAction("draft");

      let response;

      /*
      * CREATE NEW DRAFT
      */

      if (!isEditMode) {
        console.log(
          "[PaymentRequestModal] Creating payment request draft",
        );

        response = await createPaymentRequestDraft(
          payload,
        );

        console.log(
          "[PaymentRequestModal] Create draft response:",
          response,
        );

        onCreated?.(response);
      } else {
        /*
        * UPDATE EXISTING DRAFT
        */

        console.log(
          "[PaymentRequestModal] Updating payment request draft:",
          paymentRequest.id,
        );

        console.log(
          "[PaymentRequestModal] Draft update payload:",
          payload,
        );

        response = await updatePaymentRequestDraft(
          paymentRequest.id,
          payload,
        );

        console.log(
          "[PaymentRequestModal] Update draft response:",
          response,
        );

        onUpdated?.(response);
      }

      resetForm();

      onClose?.();
    } catch (err) {
      console.error(
        "[PaymentRequestModal] Failed to save payment request draft:",
        err,
      );

      console.error(
        "[PaymentRequestModal] Server response:",
        err?.response?.data,
      );

      setErrors({
        submit:
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to save draft. Please try again.",
      });
    } finally {
      setLoading(false);

      setAction(null);
    }
  };

  /*
   * ------------------------------------------------------------------------
   * UPDATE PAYMENT REQUEST
   * ------------------------------------------------------------------------
   */

  const handleUpdateRequest = async () => {
    if (loading || fetchingRequest) {
      return;
    }

    console.log(
      "[PaymentRequestModal] Update button clicked",
    );

    setErrors({});

    if (!validateForm()) {
      console.log(
        "[PaymentRequestModal] Update blocked by validation",
      );

      return;
    }

    if (!paymentRequest?.id) {
      console.error(
        "[PaymentRequestModal] Payment request ID is missing",
      );

      setErrors({
        submit:
          "Unable to update payment request: request ID is missing.",
      });

      return;
    }

    const payload = buildPayload();

    if (!payload) {
      console.error(
        "[PaymentRequestModal] Payload could not be created",
      );

      return;
    }

    console.log(
      "[PaymentRequestModal] Updating request:",
      paymentRequest.id,
    );

    console.log(
      "[PaymentRequestModal] Update payload:",
      payload,
    );

    try {
      setLoading(true);

      setAction("update");

      const response = await updatePaymentRequest(
        paymentRequest.id,
        payload,
      );

      console.log(
        "[PaymentRequestModal] Update response:",
        response,
      );

      onUpdated?.(response);

      resetForm();

      onClose?.();
    } catch (err) {
      console.error(
        "[PaymentRequestModal] Failed to update payment request:",
        err,
      );

      console.error(
        "[PaymentRequestModal] Server response:",
        err?.response?.data,
      );

      setErrors({
        submit:
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          "Unable to update payment request. Please try again.",
      });
    } finally {
      setLoading(false);

      setAction(null);
    }
  };

  /*
   * ------------------------------------------------------------------------
   * RESET FORM
   * ------------------------------------------------------------------------
   */

  const resetForm = () => {
    setSelectedClient(null);

    setForm(initialForm());

    setErrors({});

    setAction(null);

    setFetchingRequest(false);
  };

  /*
   * ------------------------------------------------------------------------
   * CLOSE
   * ------------------------------------------------------------------------
   */

  const handleClose = () => {
    if (loading || fetchingRequest) {
      return;
    }

    resetForm();

    onClose?.();
  };

  /*
   * ------------------------------------------------------------------------
   * BUSY STATE
   * ------------------------------------------------------------------------
   */

  const isBusy = loading || fetchingRequest;

  /*
   * ------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------
   */

  return (
    <div
      className="payment-modal-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div className="payment-request-modal">
        {/* ============================================================
                    HEADER
                ============================================================ */}

        <div className="payment-modal-header">
          <div>
            <span className="modal-eyebrow">PAYMENT REQUEST</span>

            <h2>
              {isEditMode ? "Edit Payment Request" : "Create Payment Request"}
            </h2>

            <p>
              {isEditMode
                ? "Update the payment request and its automatic reminders."
                : "Create a payment request and configure automatic reminders for your client."}
            </p>
          </div>

          <button
            type="button"
            className="payment-modal-close"
            onClick={handleClose}
            disabled={isBusy}
          >
            ×
          </button>
        </div>

        {/* ============================================================
                    FORM
                ============================================================ */}

        <form
          className="payment-modal-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();

            if (isEditMode) {
              handleUpdateRequest();
            } else {
              handleCreateRequest(e);
            }
          }}
        >
          {/* SERVER ERROR */}

          {errors.submit && (
            <div className="payment-form-error">{errors.submit}</div>
          )}

          {/* ========================================================
                        CONTENT
                    ======================================================== */}

          {fetchingRequest ? (
            <div className="payment-modal-loading">
              <div className="dashboard-loader" />

              <p>Loading payment request...</p>
            </div>
          ) : (
            <div className="payment-modal-content">
              {/* CLIENT */}

              <ClientSection
                selectedClient={selectedClient}
                onSelect={handleClientSelect}
                onClear={() => setSelectedClient(null)}
                error={errors.client}
              />

              {/* PAYMENT DETAILS */}

              <PaymentDetailsSection
                form={form}
                errors={errors}
                onChange={handleChange}
              />

              {/* INVOICE */}

              <InvoiceSection
                value={form.invoice}
                onChange={(invoice) =>
                  setForm((prev) => ({
                    ...prev,

                    invoice,
                  }))
                }
              />

              {/* REMINDERS */}

              <ReminderSection
                enabled={form.enable_reminders}
                reminders={form.reminders}
                errors={errors}
                onToggle={(enabled) =>
                  setForm((prev) => ({
                    ...prev,

                    enable_reminders: enabled,
                  }))
                }
                onAdd={addReminder}
                onRemove={removeReminder}
                onDuplicate={duplicateReminder}
                onUpdate={updateReminder}
              />
            </div>
          )}

          {/* ========================================================
                        FOOTER
                    ======================================================== */}

          <div className="payment-modal-footer">
            {/* SAVE AS DRAFT */}

            <button
              type="button"
              className="btn-secondary"
              onClick={handleSaveDraft}
              disabled={isBusy}
            >
              {action === "draft" ? "Saving Draft..." : "Save as Draft"}
            </button>

            {/* CANCEL */}

            <button
              type="button"
              className="btn-secondary"
              onClick={handleClose}
              disabled={isBusy}
            >
              Cancel
            </button>

            {/* CREATE / UPDATE */}

            <button type="submit" className="btn-primary" disabled={isBusy}>
              {isEditMode
                ? action === "update"
                  ? "Updating Request..."
                  : "Update Payment Request"
                : action === "create"
                  ? "Creating Request..."
                  : "Create Payment Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentRequestModal;
